import axios, { AxiosResponse } from "axios";
import { config } from "../config/config";
import { Common } from "../../common/src";
import { ProjectViDoc } from "../../common/src/modules";

// プロジェクト一覧取得(Limit付き)
export type ProjectListRequestParams = {
  status?: string;
  userId?: string;
  jobsiteId?: string;
  searchStr?: string;
  rtk?: 0 | 1 | 2;
  remove?: boolean;
  from?: string;
  to?: string;
  isPointRemoved?: boolean;
  isRTK?: 0 | 1 | 2;
  sortBy?: string;
};

// プロジェクトに配置済みの IFC オブジェクト (インスタンス) の配置データ。
// サーバーは中身を検証せず丸ごと保存/返却する (QU3DW-1368) — このシェイプは
// クライアント側で自由に決めてよい契約。
export type IfcPlacementData = {
  name: string;
  sourceUrl: string | null;
  posX: number;
  posY: number;
  posZ: number;
  rotX: number;
  rotY: number;
  rotZ: number;
  scale: number;
  pointCloudType: string;
};

// プロジェクトに配置済みの IFC オブジェクト (インスタンス) 1 件分。
// サーバー応答 (camelCase) と一致させる (QU3DW-1368 契約: objectIfcId + dataJson)。
export type PlacedIfcObject = {
  id: string;
  projectId: string;
  objectIfcId: string | null;
  dataJson: IfcPlacementData;
};

export const convertStatusStr = (statusid: string) => {
  switch (statusid) {
    case "all":
      return "all";
    case "1":
      return "UploadComplete";
    case "2":
      return "SfMComplete";
    case "3":
      return "HelmartComplete";
    case "4":
      return "VerificateComplete";
    case "5":
      return "DashboardComplete";
    case "6":
      return "ARComplete";
    default:
      return "all";
  }
};
export const fetchProjectList = (
  controller: AbortController,
  params: ProjectListRequestParams
) => {
  return new Promise<Common.ProjectListLimit>(async (resolve, reject) => {
    try {
      // 2nd arg gives a base so a relative-path serverHost ("" → "/api/v1/...")
      // still parses. URL ctor ignores base when arg #1 is absolute.
      const url = new URL(
        `${config.serverHost}/api/v1/projects`,
        window.location.origin,
      );

      // status
      params.status && url.searchParams.append("status", params.status);
      // userId
      params.userId && url.searchParams.append("userId", params.userId);
      // jobsiteId
      params.jobsiteId &&
        url.searchParams.append("jobsiteid", params.jobsiteId);
      // like
      if (params.searchStr) {
        const fullHalfConversion = params.searchStr.replace(
          /[０-９]/g,
          function (s) {
            return String.fromCharCode(s.charCodeAt(0) - 65248);
          }
        );
        url.searchParams.append("like", fullHalfConversion);
      }
      // from
      const changeUTCString = (dateStr: string, flg: boolean) => {
        const date = new Date(
          dateStr + (flg ? "T00:00:00.000" : "T23:59:59.999")
        );
        return date.toISOString();
      };
      params.from &&
        url.searchParams.append("from", changeUTCString(params.from, true));
      params.to &&
        url.searchParams.append("to", changeUTCString(params.to, false));

      params.isPointRemoved &&
        url.searchParams.append(
          "isPointRemoved",
          String(params.isPointRemoved)
        );

      params.isRTK === 1 && url.searchParams.append("isRTK", String(true));
      params.isRTK === 0 && url.searchParams.append("isRTK", String(false));

      // sortBy
      if (params.sortBy) {
        url.searchParams.append("sortBy", params.sortBy);
      }

      const res = await axios.get(url.toString(), {
        withCredentials: true,
        signal: controller.signal,
      });

      resolve(res.data);
    } catch (e) {
      reject(e);
    }
  });
};

export const fetchJobsiteList = () => {
  return new Promise<Common.LandlogJobsitesInfo>(async (resolve, reject) => {
    try {
      const url = `${config.serverHost}/api/v1/sites`;
      const res = await axios.get(url, { withCredentials: true });
      resolve(res.data);
    } catch (e) {
      reject(e);
    }
  });
};

export const fetchFileGroupWare = (type: number, site_id: string, lang?: string, signal?: AbortSignal) => {
  return new Promise<Common.LandlogFileInfoStorage>(async (resolve, reject) => {
    try {
      const url = `${config.serverHost}/api/v1/groupware`;
      const res = await axios.post(url, lang ? { type, site_id, lang } : { type, site_id }, {
        withCredentials: true,
        signal,
      });
      resolve(res.data);
    } catch (e: any) {
      // if (axios.isCancel?.(e)) {
      //   console.log("cancel request:", e.message);
      // }
      reject(e);
    }
  });
};

export const fetchUserList = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = `${config.serverHost}/api/v1/users`;
      const res = await axios.get(url, { withCredentials: true });
      resolve(res.data);
    } catch (e) {
      reject(e);
    }
  });
};

interface UploadARFileOptions {
  projectID: string;
  formData: FormData;
  onProgress?: (percent: number) => void;
}

export const reUploadARFile = async ({
  projectID,
  formData,
  onProgress,
}: UploadARFileOptions): Promise<void> => {
  await axios.post(
    `${config.serverHost}/api/v1/project/${projectID}/ar`,
    formData,
    {
      withCredentials: true,
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        if (onProgress) {
          onProgress(percent);
        }
      },
    }
  );
};

export const fetchJobSiteCoordinateSystem = (
  controller: AbortController,
  { jobsiteId }: { jobsiteId: string }
) => {
  return new Promise<ProjectViDoc>((resolve, reject) => {
    let apiUrl = `${config.serverHost}/api/v1/sites/${jobsiteId}`;
    axios
      .get(apiUrl, {
        withCredentials: true,
        signal: controller.signal,
      })
      .then((res) => {
        resolve(res.data);
      })
      .catch((e) => {
        reject(new Error(e?.message || ""));
      });
  });
};

export const projectDetailAPIs = {
  coordinate: {
    get: (projectId: string): Promise<AxiosResponse<Common.ProjectViDoc>> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/coordinate-system`,
        { withCredentials: true }
      ),

    },
    uploadStatus: {
    get: (
      projectId: string
    ): Promise<AxiosResponse<Common.ProjectViDoc>> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/processCheck`,
        { withCredentials: true }
      ),

    },
  };

export const projectPointsAPIs = {
  coordinate: {
    get: (
      projectId: string
    ): Promise<AxiosResponse<Common.PointsCoordinateData>> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/points/coordinate`,
        { withCredentials: true }
      ),
    post: (
      projectId: string,
      data: any
    ): Promise<AxiosResponse<Common.PointCoordinate[]>> =>
      axios.post(
        `${config.serverHost}/api/v1/project/${projectId}/points/coordinate`,
        data,
        { withCredentials: true }
      ),
    put: (
      projectId: string,
      data: any
    ): Promise<AxiosResponse<Common.PointCoordinate>> =>
      axios.put(
        `${config.serverHost}/api/v1/project/${projectId}/points/coordinate`,
        data,
        { withCredentials: true }
      ),
    delete: (projectId: string, data: any): Promise<AxiosResponse<void>> =>
      axios.delete(
        `${config.serverHost}/api/v1/project/${projectId}/points/coordinate`,
        { headers: { withCredentials: true }, data: data }
      ),
  },
  reference: {
    get: (
      projectId: string,
      abortControllerSignal?: AbortSignal
    ): Promise<AxiosResponse<Common.PointsReferenceData>> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/points/reference`,
        { withCredentials: true, signal: abortControllerSignal }
      ),
    post: (
      projectId: string,
      data: any
    ): Promise<AxiosResponse<Common.PointReference>> =>
      axios.post(
        `${config.serverHost}/api/v1/project/${projectId}/points/reference`,
        data,
        { withCredentials: true }
      ),
    put: (
      projectId: string,
      data: any
    ): Promise<AxiosResponse<Common.PointReference>> =>
      axios.put(
        `${config.serverHost}/api/v1/project/${projectId}/points/reference`,
        data,
        { withCredentials: true }
      ),
    delete: (projectId: string, data: any): Promise<AxiosResponse<void>> =>
      axios.delete(
        `${config.serverHost}/api/v1/project/${projectId}/points/reference`,
        { headers: { withCredentials: true }, data: data }
      ),
  },
  verification: {
    get: (
      projectId: string,
      abortControllerSignal?: AbortSignal
    ): Promise<AxiosResponse<Common.PointsVerificationData>> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/points/verification`,
        { withCredentials: true, signal: abortControllerSignal }
      ),
    post: (
      projectId: string,
      data: any
    ): Promise<AxiosResponse<Common.PointVerification>> =>
      axios.post(
        `${config.serverHost}/api/v1/project/${projectId}/points/verification`,
        data,
        { withCredentials: true }
      ),
    put: (
      projectId: string,
      data: any
    ): Promise<AxiosResponse<Common.PointVerification>> =>
      axios.put(
        `${config.serverHost}/api/v1/project/${projectId}/points/verification`,
        data,
        { withCredentials: true }
      ),
    // QU3DW-1340: `withCredentials` は axios のトップレベル設定。headers の中に
    // 入れても効かず、クロスオリジン(ローカル開発: :3000 → :3001)ではセッション
    // クッキーが送られず 401 になる。同一オリジンのSTG/本番では偶然動いていた。
    delete: (projectId: string, data: any): Promise<AxiosResponse<void>> =>
      axios.delete(
        `${config.serverHost}/api/v1/project/${projectId}/points/verification`,
        { withCredentials: true, data: data }
      ),
  },
};

export const projectARFileApis = {
  getFileUrl: {
    get: (projectId: string, fileType: string): Promise<AxiosResponse> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/ARdownload/${fileType}`,
        { withCredentials: true , 
        }
      ),
  },
  // EARTHBRAIN の IFC オブジェクト一覧取得 (認証のみ・body なし)。
  // offset / limit でページング (無限スクロール)。
  // 応答: { ifcObjects: { id, name, url }[], total, offset, limit }
  getIfcObjects: {
    get: (params?: {
      offset?: number;
      limit?: number;
    }): Promise<AxiosResponse> =>
      axios.get(`${config.serverHost}/api/v1/project/ifc-object`, {
        withCredentials: true,
        params,
      }),
  },
  // プロジェクトに配置済みの IFC オブジェクト (インスタンス) の取得 / 追加 / 削除。
  placedIfcObjects: {
    // 一覧取得。応答: { ifcObjects: PlacedIfcObject[] }
    get: (projectId: string): Promise<AxiosResponse> =>
      axios.get(`${config.serverHost}/api/v1/project/${projectId}/ifc-object`, {
        withCredentials: true,
      }),
    // 1 件追加 (body は単一オブジェクト)。応答: { ifcObjects: PlacedIfcObject[] }
    add: (projectId: string, data: any): Promise<AxiosResponse> =>
      axios.post(
        `${config.serverHost}/api/v1/project/${projectId}/ifc-object`,
        data,
        { withCredentials: true }
      ),
    // 選択した ID 群を削除 (この API 群の規約に合わせ PATCH)。
    remove: (projectId: string, ids: string[]): Promise<AxiosResponse> =>
      axios.patch(
        `${config.serverHost}/api/v1/project/${projectId}/ifc-object`,
        { ids },
        { withCredentials: true }
      ),
    // 1 件の transform (位置 / 回転 / スケール) を更新。応答: { ifcObject: PlacedIfcObject }
    update: (projectId: string, id: string, data: any): Promise<AxiosResponse> =>
      axios.put(
        `${config.serverHost}/api/v1/project/${projectId}/ifc-object/${id}`,
        data,
        { withCredentials: true }
      ),
    // スナップショット保存: シーン全体の配置済み IFC 一覧を丸ごと置き換える
    // (追加 / 更新 / 削除を 1 リクエストで)。応答: { ifcObjects: PlacedIfcObject[] }
    sync: (projectId: string, objects: any[]): Promise<AxiosResponse> =>
      axios.put(
        `${config.serverHost}/api/v1/project/${projectId}/ifc-object`,
        { objects },
        { withCredentials: true }
      ),
  },
};

export const projectPointCloudAPIs = {
  measurement: {
    get: (projectId: string, detalTab: string): Promise<AxiosResponse> =>
      axios.get(
        `${config.serverHost}/api/v1/project/${projectId}/point-cloud/${detalTab}/measurement`,
        { withCredentials: true }
      ),
  },
};

export const checkPermissionUploadGroupware = {
  uploadStatus: {
    get: (siteId: string): Promise<AxiosResponse<any>> =>
      axios.get(`${config.serverHost}/api/v1/checkGroupware?site=${siteId}`, {
        withCredentials: true,
      }),
  },
};